/**
 * modal-layers.ts — which open modal is drawn on top, the rung what renders
 * inside one hangs from, and the modals under it made inert.
 *
 * Modal renders into the body, so two open at once stack in the order they
 * opened: on one rung the later is drawn over the earlier, and a higher rung
 * over every lower one. The one on top is the one a player sees whole and can
 * press. Modal registers each one here as it opens, and what renders inside a
 * modal asks `useCovered()` whether another is drawn over it — so a clock it
 * keeps holds while it cannot be seen. use-modal-behavior.ts keeps the same
 * order for every dialog, popovers and panels too, to say which one takes the
 * keys and the focus — by the one comparator, `drawnOnTop`, so the two cannot
 * part (modal-layers.test.ts holds its order).
 *
 * Every modal under the one on top is inert, as the page under a dialog is:
 * nothing in it takes a press, a key or a screen reader, and the one-gold
 * rule does not count its gold under the scrim of the one drawn over it — the
 * year-end opening over the fleet configurator counted two. It comes back
 * whole the moment it is on top again, its focus handed back into it by the
 * one that closed over it. What hangs from a control in it — a popover, a
 * hover card, a tip — renders into the body on its own, out of its reach, so
 * it hangs here (useHang): inert with it, and while it leaves. What hangs from a
 * control on the page is the page's: it renders with the page (withPage),
 * inert with it under any modal, and is drawn on the page's rung, under the
 * dialog's scrim — rendered above the page on --z-window, it had stayed live
 * over a dialog. What hangs from a layer that stays live over the dialogs —
 * the live window, the toasts, the page top's strip — stays live with it, on
 * its rung. The page is inert while any modal is open,
 * and this list says so too (page-layers.tsx's setPageInert): the open modals
 * are counted once. Set at once, not on a render: focus going back into a
 * modal, or into the page, needs it live already. The portal's dialogs
 * settle the same way, by the same step
 * (portal-frontend/src/components/modal-layers.ts, inertUnderTop).
 */

import { createContext, useCallback, useContext, useSyncExternalStore } from 'react';
import type { CSSProperties, ReactNode, ReactPortal } from 'react';

import { abovePage, HighRung, RUNG_Z, setPageInert, withPage } from './page-layers';
import type { Rung } from './page-layers';
import { drawnOnTop, inertUnderTop } from './use-modal-behavior';

/** What a modal's overlay is to the layers: an element made inert while another is drawn over it. */
export interface LayerElement {
  inert: boolean;
}

/** The open modals, in the order they opened, each with the rung it is drawn on and its overlay. */
const layers: Array<{ id: symbol; rung: number; element: LayerElement | null }> = [];
/** What hangs from a control inside a modal — a popover, a hover card, a tip — the modal it hangs from, and whether that modal is leaving. */
const hung = new Map<LayerElement, { owner: symbol; leaving: boolean }>();
const listeners = new Set<() => void>();

/**
 * Every open modal but the one drawn on top is inert, and that one is live,
 * with what hangs from each; and the page is inert while any is open. One
 * list says all of it, so the page and the modals cannot disagree on what is
 * open.
 */
function settle() {
  inertUnderTop(layers);
  for (const [element, hang] of hung) element.inert = unseen(hang.owner, hang.leaving);
  setPageInert(layers.length > 0);
}

function changed() {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Whether another open modal is drawn over this one: it is open, and not the one on top. */
function isCovered(id: symbol): boolean {
  return layers.some((layer) => layer.id === id) && drawnOnTop(layers)?.id !== id;
}

/**
 * Whether a modal cannot be seen whole: it is leaving, or another is drawn
 * over it. What it keeps holds its clock then (useCovered), and what hangs
 * from it is inert (hangFrom) — one test, so the two cannot part.
 */
function unseen(id: symbol, leaving: boolean): boolean {
  return leaving || isCovered(id);
}

/**
 * An open modal, on its rung, until the returned function closes it: the
 * modals under it inert from now, and the one it leaves on top live again as
 * it closes. Its own overlay is left as it is then: gone, or leaving, inert
 * (Modal's way out).
 */
export function openLayer(id: symbol, rung: number, element: LayerElement | null = null): () => void {
  layers.push({ id, rung, element });
  settle();
  changed();
  return () => {
    const index = layers.findIndex((layer) => layer.id === id);
    if (index !== -1) layers.splice(index, 1);
    settle();
    changed();
  };
}

/** The modal a component renders inside, if any, and whether it is leaving: Modal provides it. */
export const ModalLayer = createContext<{ id: symbol; leaving: boolean } | null>(null);

/**
 * Whether the modal this renders inside cannot be seen whole — another is
 * drawn over it, or it is closed and leaving — false outside any modal. What
 * goes by itself holds its clock while it is true.
 */
export function useCovered(): boolean {
  const layer = useContext(ModalLayer);
  return useSyncExternalStore(subscribe, () => (layer === null ? false : unseen(layer.id, layer.leaving)));
}

/**
 * The rung of what hangs from a control on the page: over the page's panels
 * and windows, and under a dialog's scrim, which dims it as it does the page
 * it is inert with. On --z-window it stayed drawn over a dialog that opened
 * under it, undimmed and dead.
 */
const PAGE_Z = 'var(--z-page-hang)';

/**
 * A layer hanging from a control inside a modal — a popover, a hover card, a tip —
 * inert with that modal while another is drawn over it or it is leaving, and
 * live again with it, until the returned function lets it go. It renders into
 * the body on its own (abovePage), so the modal's inertness never reached it:
 * a Select's list open in the configurator stayed in the Tab order and the
 * accessibility tree under a moment's scrim.
 */
export function hangFrom(owner: symbol, element: LayerElement, leaving = false): () => void {
  hung.set(element, { owner, leaving });
  element.inert = unseen(owner, leaving);
  return () => {
    hung.delete(element);
  };
}

/** Where a hanging layer goes: what it hangs from, whether it is the page's, and its rung. */
interface Placement {
  /** The modal it hangs from, inert with it (hangFrom), or none. */
  owner: symbol | null;
  /** The page's: rendered with the page (withPage), inert with it under any modal. */
  page: boolean;
  /** Its rung, as `--hang-z`; none in a dialog, where its own --z-window draws it over its dialog. */
  z: string | undefined;
}

/**
 * Where a popover, a hover card or a tip hangs, by where its control is: the
 * modal it renders inside (ModalLayer) and the rung over the dialogs it is
 * drawn on (HighRung).
 *
 * - Inside a modal: hung from it, inert with it, above the page — on its rung
 *   if it is over the dialogs (a moment's, a notice's), else on --z-window,
 *   over its dialog.
 * - On a rung over the dialogs with no modal — the live window, the toasts'
 *   stack, the page top's strip: above the page on that rung, live with it,
 *   over what it hangs from: a hover card on a toast, on the page's rung, would
 *   hang under its toast and go dead under any dialog.
 * - With neither, on the page: the page's — the globe's filters, a hover card
 *   on the rail. It renders with the page (withPage), inert with it under any
 *   modal, on the page's rung, under the dialog's scrim; rendered above the
 *   page on --z-window, it stayed live over a dialog, its controls taking the
 *   Tab and its words the screen reader under no scrim.
 */
export function hangPlacement(layer: { id: symbol } | null, rung: Rung | null): Placement {
  if (layer) return { owner: layer.id, page: false, z: rung ? RUNG_Z[rung] : undefined };
  if (rung) return { owner: null, page: false, z: RUNG_Z[rung] };
  return { owner: null, page: true, z: PAGE_Z };
}

/** How a popover, a hover card or a tip hangs from the control that opens it (useHang). */
interface Hang {
  /** Its rung, as `--hang-z`: the layer's its control is on over the dialogs, the page's under them, or nothing in a dialog. */
  style: CSSProperties | undefined;
  /**
   * The ref its element takes, hung from the modal it renders inside
   * (hangFrom); the cleanup lets it go. A new one as that modal starts to
   * leave, so the element's ref must change with it: a ref that kept the old
   * one would leave the layer live through the modal's fade.
   */
  from: (element: HTMLElement | null) => (() => void) | undefined;
  /** Where it renders: with the page (withPage) for the page's, above it (abovePage) for one that stays live. */
  portal: (children: ReactNode) => ReactPortal;
}

/**
 * How a popover, a hover card or a tip beside its control hangs from the
 * control it opens from — one hook, so no hanging layer takes the one half
 * without the other.
 *
 * Its rung: the rung of the layer its control is on (HighRung), the page's
 * (--z-page-hang) on the page, or none in a dialog, where its own --z-window
 * draws it over the dialog. On that rung, not one over it: opened after its
 * owner, it is drawn over it, as the later of two on one rung is, and a moment
 * or a notice opening later still is drawn over it and takes the keys. One
 * rung up, it stayed over a later moment, and since use-modal-behavior ranks
 * a layer by its z-index, kept the focus and the Escape from it. The sheet
 * reads it as `--hang-z`, its own rung beneath.
 *
 * Its inertness and where it renders (hangPlacement): inside a modal, inert
 * with it while another is drawn over it or it is leaving, through the ref its
 * element takes (hangFrom), and above the page; on the page, rendered with it
 * (withPage) and inert with it under any modal; over the dialogs with no
 * modal — the live window, the toasts — above the page and live.
 */
export function useHang(): Hang {
  const rung = useContext(HighRung);
  const layer = useContext(ModalLayer);
  const place = hangPlacement(layer, rung);
  const owner = place.owner;
  const leaving = layer?.leaving ?? false;
  const from = useCallback(
    (element: HTMLElement | null) => (element && owner ? hangFrom(owner, element, leaving) : undefined),
    [owner, leaving],
  );
  return {
    style: place.z ? ({ '--hang-z': place.z } as CSSProperties) : undefined,
    from,
    // Above the page on the rung it hangs from, which what hangs from it in turn hangs on.
    portal: place.page ? withPage : (children) => abovePage(children, rung),
  };
}
